/**
 * UUID v4 для клиентских идентификаторов.
 *
 * Почему не `crypto.randomUUID()` напрямую. Он доступен ТОЛЬКО в защищённом
 * контексте: https или localhost. По голому IP по http его нет вовсе, и вызов
 * падает с `crypto.randomUUID is not a function`. На боевом стенде это ломало
 * печать целиком — этикетка не создавалась ни одна.
 *
 * Тот же случай на кухне: моноблок в локальной сети без сертификата, оболочка
 * Capacitor со старой WebView. Функция, которая есть у разработчика на localhost
 * и пропадает у клиента, — худший вид зависимости.
 *
 * ЗАПАСНОЙ ПУТЬ КРИПТОСТОЙКИЙ, и это не мелочь. Эти идентификаторы — ключи
 * идемпотентности офлайн-очереди: сервер по ним отличает повтор доставки от новой
 * печати. Совпадение двух ключей означает потерянную этикетку, а `Math.random()`
 * в цикле смены на десятке устройств совпадения даёт. `crypto.getRandomValues`
 * ограничения защищённого контекста НЕ имеет и есть везде, где есть сам `crypto`.
 */

/** Шестнадцатеричные представления байтов 0..255, посчитанные один раз. */
const HEX = Array.from({ length: 256 }, (_, index) => index.toString(16).padStart(2, '0'));

function fromRandomValues(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Версия и вариант — по RFC 4122. Без них строка выглядит как UUID, но им
  // не является: сервер, разбирающий версию, отвергнет её как невалидную.
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40; // версия 4
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80; // вариант 10xx

  const hex = Array.from(bytes, (byte) => HEX[byte] as string);
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/**
 * Новый UUID v4.
 *
 * Родная реализация предпочтительнее, когда есть: она быстрее и не требует
 * от нас правильности. Проверяется ТИП, а не наличие свойства: в некоторых
 * окружениях `randomUUID` объявлен, но не вызывается.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return fromRandomValues();
}
